import type { ReactNode } from "react";
import type { DashboardParameterValue, DashboardWidget } from "../types";
import { sortWidgetsByPosition } from "../types";
import { isWidgetVisible } from "../views/parameters";
import styles from "./WidgetGrid.module.css";

export interface WidgetGridProps {
  widgets: DashboardWidget[];
  /**
   * When set, widgets hidden by `requires` are omitted (no grid slot).
   * Callers should render {@link DashboardWidgetTile} with the same map and
   * `visibilityPrechecked` so the tile does not re-run {@link isWidgetVisible}
   * (a `<DashboardWidgetTile />` element is always truthy, so the grid cannot
   * infer gating from the render prop alone).
   */
  resolvedParams?: Record<string, DashboardParameterValue>;
  children: (widget: DashboardWidget) => ReactNode;
}

export function WidgetGrid({ widgets, resolvedParams, children }: WidgetGridProps) {
  return (
    <div className={styles.grid} data-testid="widget-grid">
      {sortWidgetsByPosition(widgets).map((widget) => {
        if (resolvedParams !== undefined && !isWidgetVisible(widget, resolvedParams)) {
          return null;
        }
        const rendered = children(widget);
        // When `resolvedParams` is omitted, callers may return null from
        // the render prop to skip a slot (tests, custom tiles).
        if (rendered === null || rendered === undefined || rendered === false) {
          return null;
        }
        return (
          <div
            key={widget.id}
            className={`${styles.item} ${widget.layout.width === 12 ? styles.full : styles.half}`}
            data-testid={`widget-grid-item-${widget.id}`}
          >
            {rendered}
          </div>
        );
      })}
    </div>
  );
}
